import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { ProtectedRoute } from './auth/RequirePermission';
import { ChatPage } from './pages/ChatPage';
import { ErrorBoundary } from './components/error/ErrorBoundary';

// Code-split the bundle: login is a separate flow, home is the landing page,
// dashboard is rarely-clicked, admin is admin-only, 404 is a leaf. Each
// becomes its own chunk.
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const BriefingPage = lazy(() =>
  import('./pages/BriefingPage').then((m) => ({ default: m.BriefingPage })),
);
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const TicketsPage = lazy(() =>
  import('./pages/TicketsPage').then((m) => ({ default: m.TicketsPage })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

function PageFallback() {
  return <div className="flex h-full items-center justify-center text-slate-400">Loading…</div>;
}

export function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />
          {/* Post-login landing surface — branded home page introducing
              the four executives. Replaces the old auto-redirect into a
              ChatPage that would create-or-pick the most-recent conversation. */}
          <Route
            path="/"
            element={user ? <HomePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/c/:conversationId"
            element={user ? <ChatPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/c/:conversationId/dashboard"
            element={user ? <DashboardPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/briefing"
            element={user ? <BriefingPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/admin"
            element={user ? <AdminPage /> : <Navigate to="/login" replace />}
          />
          {/* COO maqueta — gated by real RBAC. ProtectedRoute redirects home
              if the user lacks the capability (UX only; the API enforces too). */}
          <Route
            path="/tickets"
            element={
              <ProtectedRoute cap="tickets.view">
                <TicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute cap="integrations.dashboard.view">
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
